import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Music } from "lucide-react";
import { Button } from "@/components/ui/button";
import occasionQuinceanera from "@/assets/occasion-quinceanera.png";
import occasionWeddingFD from "@/assets/occasion-wedding-father-daughter.png";
import occasionToddler from "@/assets/occasion-toddler.png";
import occasionBabyShower from "@/assets/occasion-baby-shower.png";
import occasionGymnastics from "@/assets/occasion-gymnastics.png";
import occasionAlwaysWithYou from "@/assets/occasion-always-with-you.png";

interface OccasionSample {
  label: string;
  songTitle: string;
  occasion: string;
  description: string;
  gradient: string;
  audioSrc: string;
  image?: string;
}

const occasions: OccasionSample[] = [
  {
    label: "Wedding",
    songTitle: "Always Your Little Girl",
    occasion: "Father–Daughter First Dance",
    description:
      "A heartfelt first dance song celebrating Ximena and her lifelong bond with her father.",
    gradient: "from-[hsl(328,26%,45%)] to-[hsl(340,30%,60%)]",
    audioSrc: "/audio/ximena-wedding-daddy-daughter.mp3",
    image: occasionWeddingFD,
  },
  {
    label: "Wedding",
    songTitle: "Always With You",
    occasion: "Mother–Son Dance",
    description:
      "An upbeat and joyful mother-son dance for Patrick that celebrates laughter, pride, and growing up together.",
    gradient: "from-[hsl(50,38%,50%)] to-[hsl(40,35%,55%)]",
    audioSrc: "/audio/patrick-always-with-you.mp3",
    image: occasionAlwaysWithYou,
  },
  {
    label: "Gymnastics Floor Routine",
    songTitle: "Built Different",
    occasion: "Gymnastics Floor Routine — Delilah",
    description:
      "A powerful, high-energy rap designed for a competitive gymnastics floor routine.",
    gradient: "from-[hsl(328,26%,50%)] to-[hsl(350,30%,55%)]",
    audioSrc: "/audio/floor-routine.mp3",
    image: occasionGymnastics,
  },
  {
    label: "Baby Shower",
    songTitle: "Worth the Wait",
    occasion: "Baby Shower — Anil & Neha",
    description:
      "A gentle and emotional song honoring the IVF journey and the patience, strength, and hope that led to a miracle baby.",
    gradient: "from-[hsl(17,21%,35%)] to-[hsl(328,20%,45%)]",
    audioSrc: "/audio/you-were-worth-the-wait.mp3",
    image: occasionBabyShower,
  },
  {
    label: "Quinceañera",
    songTitle: "Canción de Quinceañera",
    occasion: "Quinceañera — Valeria",
    description:
      "Una canción emotiva que celebra los quince años de Valeria y su camino hacia la juventud.",
    gradient: "from-[hsl(340,30%,50%)] to-[hsl(50,38%,55%)]",
    audioSrc: "/audio/cancion-de-quincea-valeria.mp3",
    image: occasionQuinceanera,
  },
  {
    label: "Just Because",
    songTitle: "Millie's Volcano Feelings Song",
    occasion: "Toddler Affirmation Song",
    description:
      "A calming and reassuring affirmation song for Millie, a toddler with big emotions.",
    gradient: "from-[hsl(17,21%,30%)] to-[hsl(17,18%,45%)]",
    audioSrc: "/audio/millie-volcano-feelings.mp3",
    image: occasionToddler,
  },
];

const OccasionsSection = () => {
  const [playingIdx, setPlayingIdx] = useState<number | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [activeDuration, setActiveDuration] = useState<number>(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const rafRef = useRef<number>(0);

  const tick = () => {
    if (audioRef.current) {
      setProgress(audioRef.current.currentTime);
      setActiveDuration(audioRef.current.duration || 0);
    }
    rafRef.current = requestAnimationFrame(tick);
  };

  const toggle = (idx: number) => {
    if (playingIdx === idx) {
      audioRef.current?.pause();
      cancelAnimationFrame(rafRef.current);
      setPlayingIdx(null);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      cancelAnimationFrame(rafRef.current);
    }

    const audio = new Audio(occasions[idx].audioSrc);
    audioRef.current = audio;
    audio.play();
    setPlayingIdx(idx);
    setProgress(0);
    rafRef.current = requestAnimationFrame(tick);

    audio.addEventListener("ended", () => {
      setPlayingIdx(null);
      setProgress(0);
      cancelAnimationFrame(rafRef.current);
    });
  };

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const fmt = (s: number) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="listen" className="pt-10 pb-5 md:pt-14 md:pb-7 bg-primary">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
            Songs for Every Meaningful Moment
          </h2>
          <div className="section-divider mb-6" aria-hidden="true" />
          <p className="text-primary-foreground/80 font-body max-w-xl mx-auto text-xl">
            Some moments deserve more than a card.
            <br />
            They deserve a song. Listen to real songs we created.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto mb-10">
          {occasions.map((occ, i) => {
            const isPlaying = playingIdx === i;
            return (
              <motion.div
                key={occ.label}
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                whileInView={{
                  opacity: 1,
                  scale: 1,
                  y: 0,
                }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.5,
                  delay: i * 0.08,
                  type: "spring",
                  stiffness: 500,
                  damping: 25,
                }}
                whileHover={{
                  scale: 1.05,
                  transition: { type: "spring", stiffness: 400, damping: 10 },
                }}
                role="button"
                tabIndex={0}
                aria-label={`${isPlaying ? "Pause" : "Play"} ${occ.songTitle} — ${occ.occasion}`}
                className="group relative overflow-hidden rounded-2xl aspect-[4/3] cursor-pointer text-left"
                onClick={() => toggle(i)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(i); } }}
              >
                {/* Background */}
                {occ.image ? (
                  <img src={occ.image} alt={`${occ.songTitle} — ${occ.occasion}`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <div className={`absolute inset-0 bg-gradient-to-br ${occ.gradient} transition-transform duration-500 group-hover:scale-105`} />
                )}
                <div className="absolute inset-0 bg-foreground/30 group-hover:bg-foreground/20 transition-colors" />

                {/* Play button */}
                <div className="absolute top-4 right-4 z-20">
                  <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30 group-hover:bg-white/30 transition-colors">
                    {isPlaying ? (
                      <Pause className="w-5 h-5 text-white" aria-hidden="true" />
                    ) : (
                      <Play className="w-5 h-5 text-white ml-0.5" aria-hidden="true" />
                    )}
                  </div>
                </div>

                {/* Progress bar at bottom of card */}
                {isPlaying && activeDuration > 0 && (
                  <div className="absolute bottom-0 left-0 right-0 z-20 h-1 bg-white/20">
                    <div
                      className="h-full bg-white/80 transition-[width] duration-100"
                      style={{ width: `${(progress / activeDuration) * 100}%` }}
                    />
                  </div>
                )}

                {/* Content */}
                <div className="relative z-10 flex flex-col justify-end h-full p-5">
                  <span className="text-white/60 text-sm font-body uppercase tracking-wider mb-1">
                    {occ.label}
                  </span>
                  <h3 className="font-heading text-lg md:text-xl font-bold text-white mb-0.5">
                    {occ.songTitle}
                  </h3>
                  <p className="text-white/70 text-sm font-body flex items-center gap-1">
                    <Music className="w-3 h-3" aria-hidden="true" />
                    {occ.occasion}
                  </p>
                  {isPlaying && (
                    <p className="text-white/50 text-sm font-body mt-1">
                      {fmt(progress)} / {fmt(activeDuration)}
                    </p>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default OccasionsSection;
